import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBoolean } from 'usehooks-ts';

import { updateEXCPersonalInfo } from '@/api/user';

import { useAppSelector } from '@/hooks/redux';
import { useUpdateUser } from '@/hooks/useUpdateUser';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import {
  selectConfigsState,
  selectMailingAddressFeature,
} from '@/redux/configs';

import { UpdateInfoRequestPayload } from '@/types/api/user';
import { CountryISO } from '@/types/places';
import { EmploymentStatus, User } from '@/types/user';

import {
  getInitialMailingSameAsResidential,
  getMailingAddressFromUser,
  getNormalizedMailingAddress,
  getTenantDefaultValues,
} from '@/utils/address';
import { getCorrectCountryCode } from '@/utils/countryTranslations';
import { getRiskSectorFlag } from '@/utils/employment';
import { getFormDirtyValues } from '@/utils/form';
import { convertToBoolean, emptyStringIfNull } from '@/utils/string';
import {
  extractLocalPhoneNumber,
  getNormalizedTaxResidences,
  getOtherTaxCountries,
  getOtherTins,
  isRemediationCustomer,
  shouldShowTinForCountry,
} from '@/utils/user';

import { UpdatePersonalInfo } from '../types';
import { updatePersonalInfoFormToAddressFormForMailing } from '../utils/address';

type UseUpdatePersonalInfoProps = {
  triggerFormValidation?: boolean;
  onSuccess?: () => void;
};

export const useUpdatePersonalInfo = ({
  triggerFormValidation,
  onSuccess,
}: UseUpdatePersonalInfoProps) => {
  const [rawPayload, setRawPayload] = useState<UpdatePersonalInfo>(
    {} as UpdatePersonalInfo,
  );
  const updateUser = useUpdateUser();
  const {
    value: isOpenConfirmPopup,
    setTrue: openConfirmPopup,
    setFalse: closeConfirmPopup,
  } = useBoolean(false);
  const user = useAppSelector(selectLoggedInUser) as User;
  const { requiredLocalTIN, isoCode: localISO } =
    useAppSelector(selectConfigsState);
  const shouldAddMailingAddress = useAppSelector(selectMailingAddressFeature);

  const otherTins = useMemo(
    () => getOtherTins(user?.tin, [CountryISO.US, localISO]),
    [user?.tin, localISO],
  );

  const { tenant, phoneNumber } = extractLocalPhoneNumber(
    user.phoneNumber || '',
  );
  const { tenant: employerTenant, phoneNumber: employerPhoneNumber } =
    extractLocalPhoneNumber(user.employmentDetailed?.employerPhone || '');
  const countryCode = getCorrectCountryCode(user?.addressDetailed?.country);

  const normalizedTaxResidences = useMemo(
    () => getNormalizedTaxResidences(user?.taxResidences),
    [user?.taxResidences],
  );

  const isDifferentTaxResidency = useMemo(() => {
    if (normalizedTaxResidences.length > 1) {
      return true;
    }

    if (normalizedTaxResidences.length === 1) {
      return normalizedTaxResidences[0] !== user?.addressDetailed?.country;
    }

    return false;
  }, [normalizedTaxResidences, user?.addressDetailed?.country]);

  const mailingFormDefaults = useMemo(() => {
    if (!shouldAddMailingAddress) {
      return {};
    }
    const tenantDefaults = getTenantDefaultValues(user.tenant, user);
    const mailing = getMailingAddressFromUser(user);
    return {
      mailingSameAsResidential: getInitialMailingSameAsResidential(user),
      mailingLine1: mailing?.line1 ?? '',
      mailingCity: mailing?.city ?? '',
      mailingState: mailing?.state ?? '',
      mailingZip: mailing?.zip ?? '',
      mailingCountry: mailing?.country
        ? getCorrectCountryCode(mailing.country)
        : '',
      residenceCountry:
        tenantDefaults.residenceCountry ?? user.residenceCountry ?? '',
    };
  }, [shouldAddMailingAddress, user]);

  const methods = useForm<UpdatePersonalInfo>({
    defaultValues: {
      countryCode: tenant || '',
      phoneNumber: phoneNumber,
      citizenship: user.citizenship,
      addressDetailed: {
        line1: user.addressDetailed.line1 || '',
        line2: user.addressDetailed.line2 || '',
        city: user.addressDetailed.city || '',
        state: user.addressDetailed.state || '',
        zip: user.addressDetailed.zip || '',
        country: countryCode,
      },
      employmentDetailed: {
        employerAddress: user.employmentDetailed?.employerAddress || '',
        employerName: user.employmentDetailed?.employerName || '',
        occupationCode: user.employmentDetailed?.occupationCode || '',
        startDate: user.employmentDetailed?.startDate || '',
        employmentDesc: user.employmentDetailed?.employmentDesc || '',
        employmentStatus:
          user.employmentDetailed?.employmentStatus || ('' as EmploymentStatus),
        riskSectorFlag: user.employmentDetailed?.riskSectorFlag,
        riskSectorCategory: user.employmentDetailed?.riskSectorCategory || null,
        isStartup: user.employmentDetailed?.isStartup,
        employerPhone: employerPhoneNumber,
        employerPhoneCode: employerTenant || '',
        employerCountry: user.employmentDetailed?.employerCountry || '',
        businessCategoryCode: emptyStringIfNull(
          user.employmentDetailed?.businessCategoryCode,
        ),
      },
      taxResidences: normalizedTaxResidences || [],
      localTin: user.tin?.[localISO] || '',
      differentTaxResidency: isDifferentTaxResidency,
      usTin: user.tin?.[CountryISO.US] || '',
      otherTins,
      ...mailingFormDefaults,
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { isValid, dirtyFields, defaultValues },
    watch,
    reset,
    trigger,
    setError,
    getValues,
  } = methods;

  useEffect(() => {
    if (triggerFormValidation) {
      trigger();
    }
  }, [trigger, triggerFormValidation]);

  const taxResidences = watch('taxResidences');

  const { mutate, isPending } = useMutation({
    mutationFn: updateEXCPersonalInfo,
    onError: (error: Record<string, { error: { message: string } }>) => {
      Object.entries(error).forEach(([key, value]) => {
        setError(key as keyof UpdatePersonalInfo, {
          message: value.error.message,
        });
      });
    },
    onSuccess: () => {
      reset();
      onSuccess?.();
      updateUser();
    },
  });

  const { shouldShowOtherTins, shouldShowLocalTin, shouldShowUsTin } =
    useMemo(() => {
      const shouldShowLocalTin =
        requiredLocalTIN &&
        shouldShowTinForCountry(user, taxResidences, localISO as CountryISO);
      const otherTaxCountries = shouldShowLocalTin
        ? getOtherTaxCountries(taxResidences, [CountryISO.US, localISO])
        : getOtherTaxCountries(taxResidences, [CountryISO.US]);
      return {
        shouldShowUsTin: shouldShowTinForCountry(
          user,
          taxResidences,
          CountryISO.US,
        ),
        shouldShowLocalTin,
        shouldShowOtherTins: otherTaxCountries?.length,
      };
    }, [localISO, requiredLocalTIN, taxResidences, user]);

  const prepareTINPayload = (
    payload: Omit<Partial<UpdatePersonalInfo>, 'employmentDetailed'>,
  ) => {
    const { usTin, localTin, otherTins } = payload;
    const tin: Record<string, string> = {};
    if (usTin && shouldShowUsTin) {
      tin[CountryISO.US] = usTin;
    }
    if (localTin && shouldShowLocalTin) {
      tin[localISO] = localTin;
    }
    if (shouldShowOtherTins && otherTins) {
      for (const code of Object.keys(otherTins)) {
        if (otherTins[code]) {
          tin[code] = otherTins[code];
        }
      }
    }
    return tin;
  };

  // Build mailing from the full form — dirty-only fields omit unchanged mailing values
  const getMailingAddressPayload = (
    contactInfoPayload: Record<string, unknown>,
  ) => {
    const mailingAddress = getNormalizedMailingAddress(
      updatePersonalInfoFormToAddressFormForMailing(getValues(), user),
      user.tenant,
      { shouldAddMailingAddress: true },
    );
    if (mailingAddress !== undefined) {
      return { ...contactInfoPayload, mailingAddress };
    }
    return contactInfoPayload;
  };

  const prepareContactInfoPayload = (
    payload: Omit<Partial<UpdatePersonalInfo>, 'employmentDetailed'>,
  ) => {
    const {
      addressDetailed,
      usTin,
      localTin,
      otherTins,
      // Mailing address is depended on roll out tenant, omit to make sure non-roll out tenants are not affected
      mailingSameAsResidential: _omitMailingSame,
      mailingLine1: _omitMailingLine1,
      mailingCity: _omitMailingCity,
      mailingState: _omitMailingState,
      mailingZip: _omitMailingZip,
      mailingCountry: _omitMailingCountry,
      ...restPayload
    } = payload;
    const otherFields: {
      tin: { [key: string]: string };
      addressDetailed: { [key: string]: unknown };
    } = {
      tin: {},
      addressDetailed: { ...addressDetailed }, // Map AddressDetailed to otherFields.addressDetailed for delete line1 if it is not changed from the payload to avoid updating the address
    };

    const calculatedTin = prepareTINPayload(payload);
    otherFields.tin = { ...calculatedTin };

    if (addressDetailed?.line1) {
      const { line1 } = addressDetailed;
      if (line1 === defaultValues?.addressDetailed?.line1) {
        // GoogleMaps Autocomplete will trigger change when setting default values from react-hook-form
        // We need to remove the line1 if it is not changed from the payload to avoid updating the address
        delete otherFields.addressDetailed.line1;
      }
    }

    const {
      addressDetailed: formAddressDetailed,
      tin: formTin,
      ...rest
    } = otherFields;

    const normalizedTaxResidences = Array.isArray(payload.taxResidences)
      ? payload.taxResidences.join(',')
      : payload.taxResidences;

    const contactInfoPayload = {
      ...restPayload,
      ...(normalizedTaxResidences
        ? { taxResidences: normalizedTaxResidences }
        : {}),
      ...(Object.keys(formAddressDetailed).length > 0
        ? { addressDetailed: formAddressDetailed }
        : {}),
      ...(Object.keys(formTin).length > 0 ? { tin: formTin } : {}),
      ...rest,
    };

    // #region mailing address
    if (shouldAddMailingAddress && !isRemediationCustomer(user)) {
      return getMailingAddressPayload(contactInfoPayload);
    }
    // #endregion mailing address

    return contactInfoPayload;
  };

  const onConfirm = () => {
    // #region contact details data
    const { employmentDetailed, ...personalInfoPayload } =
      getFormDirtyValues<UpdatePersonalInfo>(
        rawPayload,
        dirtyFields as Record<Partial<keyof UpdatePersonalInfo>, unknown>,
      ) as UpdatePersonalInfo;

    const payload = prepareContactInfoPayload(
      personalInfoPayload,
    ) as Partial<UpdateInfoRequestPayload>;
    // #endregion contact details data
    const isStartup = convertToBoolean(employmentDetailed?.isStartup);
    mutate({
      infoRequest: payload as UpdateInfoRequestPayload,
      employmentDetailed: {
        currentUser: user,
        employmentDetailed: {
          ...employmentDetailed,
          riskSectorFlag: getRiskSectorFlag(
            employmentDetailed?.riskSectorFlag,
            employmentDetailed?.riskSectorCategory,
          ),
          isStartup,
        },
      },
    });
    closeConfirmPopup();
  };

  const onSubmit = handleSubmit(rawPayload => {
    setRawPayload({ ...rawPayload });
    openConfirmPopup();
  });

  return {
    onConfirm,
    onSubmit,
    isPending,
    isValid,
    isOpenConfirmPopup,
    closeConfirmPopup,
    openConfirmPopup,
    methods,
    resetForm: reset,
  };
};
